// File: src/pages/Bouncer.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Html5QrcodeScanner, Html5QrcodeScanType } from 'html5-qrcode';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

// LocalStorage key for bouncer session cache
const LSK = 'bouncerSession_v1';




// Helpers to persist/restore session for whole event window
function saveSession({ eventId, code, endISO }) {
  try {
    const payload = {
      eventId: String(eventId || '').trim(),
      code: String(code || '').trim(),
      endISO: endISO || null,
      savedAt: Date.now(),
    };
    localStorage.setItem(LSK, JSON.stringify(payload));
  } catch {}
}
function loadSession() {
  try {
    const raw = localStorage.getItem(LSK);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.eventId || !obj?.code) return null;
    if (obj?.endISO) {
      // Only allow if still inside the event window
      const now = Date.now();
      const endMs = new Date(obj.endISO).getTime();
      if (Number.isFinite(endMs) && now > endMs) {
        // expired event window
        localStorage.removeItem(LSK);
        return null;
      }
    }
    return obj;
  } catch { return null; }
}
function clearSession() {
  try { localStorage.removeItem(LSK); } catch {}
}

export default function Bouncer() {
  const [eventId, setEventId]   = useState('');
  const [code, setCode]         = useState('');
  const [armed, setArmed]       = useState(false);
  const [result, setResult]     = useState(null);
  const scanLockUntilRef = useRef(0);   // soft lock during in-flight processing (~1s)

  // diagnostics (kept but we’ll comment out the UI)
  const [verifyDiag, setVerifyDiag] = useState(null);
  const [scanDiag, setScanDiag]     = useState(null);

  // banner state: '', 'green', 'red'
  const [banner, setBanner] = useState('');

  // refs (no re-renders)
  const scannerRef       = useRef(null);
  const holdTimer        = useRef(null);
  const cooldownUntilRef = useRef(0);   // timestamp (ms) until which scans are ignored
  const lastTokenRef     = useRef(null);
  const lastScanAtRef    = useRef(0);
  const autoTriedRef     = useRef(false);

  const clearTimers = () => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };

  const startCooldown = (token, ms = 5000, bannerKind = '') => {
    // set global cooldown
    const now = Date.now();
    cooldownUntilRef.current = now + ms;
    lastTokenRef.current = token ?? null;
    lastScanAtRef.current = now;

    // show banner for same duration
    setBanner(bannerKind);
    clearTimers();
    holdTimer.current = setTimeout(() => {
      setBanner('');
      setResult(null);
    }, ms);

    // haptics
    try {
      if (bannerKind === 'green') window.navigator?.vibrate?.(100);
      else if (bannerKind === 'red') window.navigator?.vibrate?.([60, 30, 60]);
    } catch {}
  };

  // Auto-arm from saved session on first mount
  useEffect(() => {
    if (autoTriedRef.current) return;
    autoTriedRef.current = true;
    const saved = loadSession();
    if (!saved) return;
    // silently verify to re-arm without asking for code
    (async () => {
      try {
        const res = await fetch(`${API}/bouncer/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ eventId: saved.eventId, code: saved.code }),
        });
        const data = await res.json();
        if (res.ok && data?.ok) {
          // hydrate fields & arm
          setEventId(saved.eventId);
          setCode(saved.code);
          setVerifyDiag(data?.diag || null);
          setArmed(true);
        } else {
          clearSession(); // invalid or window closed
        }
      } catch {
        // ignore; user can type manually
      }
    })();
  }, [API]);

  useEffect(() => {
    if (!armed || !eventId || !code) return;

    const id = 'qr-reader';

    // Create scanner ONCE per arm/session (no re-creation on banner/cooldown changes)
    if (!scannerRef.current) {
      const qrbox = (viewfinderWidth, viewfinderHeight) => {
        const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
        const size = Math.floor(minEdge * 0.85);
        return { width: size, height: size };
      };
      const config = {
        fps: 24,
        qrbox,
        aspectRatio: 1.0,
        rememberLastUsedCamera: true,
        supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        showZoomSliderIfSupported: true,
        showTorchButtonIfSupported: true,
      };

      const scanner = new Html5QrcodeScanner(id, config, false);
      scannerRef.current = scanner;

      const onScan = async (decodedText) => {
        const now = Date.now();
      
        // Ignore while in cooldown window or while processing a recent scan
        if (now < cooldownUntilRef.current || now < scanLockUntilRef.current) return;
      
        // Extract token (supports full URL with ?t=)
        let token = decodedText;
        try {
          const u = new URL(decodedText);
          const t = u.searchParams.get('t');
          if (t) token = t;
        } catch (_) {}
      
        // Dedupe: if same token seen very recently, ignore
        if (lastTokenRef.current && lastTokenRef.current === token && (now - lastScanAtRef.current) < 15000) {
          return;
        }
      
        // 🔒 Preemptive short lock so html5-qrcode's rapid callbacks don't double-fire a second request
        scanLockUntilRef.current = now + 1200;   // ~1.2s
        lastTokenRef.current = token;            // set dedupe token immediately
        lastScanAtRef.current = now;
      
        setScanDiag({ ts: now, decodedText });
      
        try {
          const res = await fetch(`${API}/bouncer/scan`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ eventId: eventId.trim(), code: code.trim(), t: token }),
          });
          const json = await res.json();
      
          if (json.scannedAt && !json.scannedAtISO) json.scannedAtISO = json.scannedAt;
      
          // Show banners and extend to the full 5s cooldown
          setResult(json);
          if (json?.verdict === 'green') {
            startCooldown(token, 5000, 'green');
            return; // stop immediately
          } else {
            startCooldown(token, 5000, 'red');
            return;
          }
          
        } catch {
          setResult({ verdict: 'red', reason: 'network_error' });
          startCooldown(token, 5000, 'red');
        }
      };
      

      const onError = () => { /* ignore frame decode errors for speed */ };

      // Render the scanner UI once (no repeated permission prompts)
      scanner.render(onScan, onError);
    }

    // cleanup ONLY when disarming or leaving page
    return () => {
      clearTimers();
      if (!armed && scannerRef.current?.clear) {
        try { scannerRef.current.clear(); } catch {}
      }
      // do NOT null scannerRef on unmount while armed; camera stays mounted during session
    };
  }, [armed, eventId, code]);

  async function verifyAndArm() {
    setResult(null);
    setScanDiag(null);
    setVerifyDiag(null);
    setBanner('');
    cooldownUntilRef.current = 0;
    lastTokenRef.current     = null;
    lastScanAtRef.current    = 0;

    const body = { eventId: String(eventId || '').trim(), code: String(code || '').trim() };
    if (!body.eventId || !body.code) return;

    try {
      const res = await fetch(`${API}/bouncer/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setVerifyDiag(data?.diag || null);

      if (res.ok && data?.ok) {
        setArmed(true); // mounts/keeps the camera; we do NOT unmount after scans

        // Persist session through event end so refreshes don't prompt
        const endISO = data?.diag?.endISO || null;
        saveSession({ eventId: body.eventId, code: body.code, endISO });
      } else {
        setArmed(false);
        clearSession();
        const msg =
          data?.reason === 'out_of_window'
            ? `Event not live. ${data?.eventDetails?.time ? `Window: ${data.eventDetails.time}` : ''}`
            : (data?.reason || 'Invalid');
        alert(String(msg).replaceAll('_', ' '));
      }
    } catch {
      setVerifyDiag(null);
      setArmed(false);
      alert('Network error');
    }
  }

  // ───────────────────────── Views ─────────────────────────

  if (!armed) {
    return (
      <Center>
        {/* White Tribe logo at top */}
        <style>{`
          .tribe-white :is(path,circle,rect,polygon,polyline,ellipse) { fill: #fff !important; }
          .tribe-white [fill="none"] { fill: none !important; }
          .tribe-white * { stroke: none !important; }
        `}</style>
        <div style={{ marginBottom: 16 }}>
          <TribeLogo className="tribe-white" style={{ width: 160, height: 'auto' }} />
        </div>

        <h1 style={{ margin: '8px 0 4px' }}>BOUNCER MODE</h1>
        <input
          value={eventId}
          onChange={e => setEventId(e.target.value)}
          placeholder="Event ID"
          style={S.input}
        />
        <input
          value={code}
          onChange={e => setCode(e.target.value)}
          placeholder="Bouncer Code (6 digits)"
          style={S.input}
        />
        <button
          style={S.btn}
          disabled={!eventId || !code}
          onClick={verifyAndArm}
        >
          Unlock Scanner
        </button>

        {/* Debug panel for VERIFY (commented out) */}
        {/*
        <DebugPanel title="Verify Debug" diag={verifyDiag} />
        */}
      </Center>
    );
  }

  const isGreen = result?.verdict === 'green';

  // Build banner message (local time only for red)
  let msg;
  if (isGreen) {
    msg = 'Admit';
  } else {
    const iso = result?.scannedAtISO || null;
    if (iso) {
      const localTime = new Date(iso).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      });
      msg = `Already scanned at ${localTime}`;
    } else if (result?.reason?.startsWith('already_scanned_at_')) {
      // Fallback for older payloads that embed a time string
      msg = result.reason.replace('already_scanned_at_', 'Already scanned at ');
    } else {
      msg = result?.reason || 'Invalid';
    }
  }

  // Full-screen background color accent while banner is visible
  const bgAccent =
  banner === 'green' ? '#22c55e'   // bright green
  : banner === 'red' ? '#ef4444'   // bright red
  : '#000';  

    return (
        <div style={{ minHeight: '100vh', background: bgAccent, color: '#fff', padding: 16 }}>
          {/* Big Tribe logo at top in armed view too */}
          <style>{`
            .tribe-white :is(path,circle,rect,polygon,polyline,ellipse) { fill: #fff !important; }
            .tribe-white [fill="none"] { fill: none !important; }
            .tribe-white * { stroke: none !important; }
          `}</style>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <TribeLogo className="tribe-white" style={{ width: 140, height: 'auto' }} />
          </div>
      
      <div id="qr-reader" style={{ maxWidth: 640, margin: '0 auto' }} />

      {/* Green banner (5s) */}
      {result && banner === 'green' && isGreen && (
        <div
          style={{
            ...S.banner,
            background: '#DCFCE7',
            color: '#065F46',
            border: '1px solid #A7F3D0',
          }}
        >
          <div style={S.title}>{msg}</div>
          <div style={S.sub}>{result?.eventName || ''}</div>
          <div style={S.meta}>{result?.guest?.name || ''}</div>
          <div style={S.meta}>{result?.eventDetails?.time || ''}</div>
          <div style={S.meta}>{result?.eventDetails?.where || ''}</div>
        </div>
      )}

      {/* Red banner (5s) */}
      {result && banner === 'red' && !isGreen && (
        <div
          style={{
            ...S.banner,
            background: '#FEE2E2',
            color: '#7F1D1D',
            border: '1px solid #FECACA',
          }}
        >
          <div style={S.title}>{msg}</div>
          <div style={S.sub}>{result?.eventName || ''}</div>
          <div style={S.meta}>{result?.guest?.name || ''}</div>
          <div style={S.meta}>{result?.eventDetails?.time || ''}</div>
          <div style={S.meta}>{result?.eventDetails?.where || ''}</div>
        </div>
      )}

      {/* Debug panel for SCAN (commented out) */}
      {/*
      <DebugPanel title="Scan Debug" diag={scanDiag} />
      */}
    </div>
  );
}

/* ---------- Debug panel component (kept, but not rendered) ---------- */
function DebugPanel({ title, diag }) {
  return (
    <div style={{
      margin: '16px auto',
      maxWidth: 640,
      background: '#0f172a',
      border: '1px solid #334155',
      borderRadius: 12,
      padding: 12,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: 12,
      color: '#fff'
    }}>
      <div style={{ fontWeight: 800, marginBottom: 8 }}>{title}</div>
      {diag ? (
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 4, columnGap: 8 }}>
          <label>nowISO</label><code>{new Date(diag.ts).toISOString()}</code>
          <label>decoded</label><code>{diag.decodedText}</code>
        </div>
      ) : (
        <div style={{ opacity: 0.6 }}>No diagnostics available</div>
      )}
    </div>
  );
}

function Center({ children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0b0b0b', color: '#fff' }}>
      <div style={{ textAlign: 'center', width: 360 }}>{children}</div>
    </div>
  );
}

const S = {
  input:  { width: '100%', padding: '10px 12px', margin: '8px 0', borderRadius: 8, border: '1px solid #333', background: '#111', color: '#fff', boxSizing: 'border-box' },
  btn:    { width: '100%', padding: '12px', borderRadius: 8, border: 'none', fontWeight: 800, background: '#2563EB', color: '#fff', cursor: 'pointer' },
  banner: {
    position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)',
    minWidth: 280, maxWidth: '90vw',
    color: '#fff', padding: '14px 16px', textAlign: 'center',
    borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.2)', zIndex: 1000
  },
  title:  { fontSize: 24, fontWeight: 900 },
  sub:    { marginTop: 4, opacity: .95, fontWeight: 700 },
  meta:   { marginTop: 2, opacity: .85, fontSize: 13 },
};
